import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
} from '@angular/core';

import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from 'environments/environment';
import {
  AppUpdateService,
  AuthInterceptor,
  AuthService,
  GlobalErrorHandler,
  PostHogService,
  StateService,
} from '@cosmos/angular/utils';
import { initConfig } from './initConfig';
import { initPostHog } from './initPostHog';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { getInitialPreset } from './custom-presets';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MessageService } from 'primeng/api';
import { provideFormlyCore } from '@ngx-formly/core';
import { withFormlyPrimeNG } from '@ngx-formly/primeng';
import {
  FormlyChipsNumberComponent,
  GroupWrapper,
  InputNumberFieldCosmos,
  InputTextFieldCosmos,
  MultiSelectFieldCosmos,
  PrimeNGCheckboxWrapper,
  PrimeNGDefaultWrapper,
  PrimeNGFloatLabelWrapper,
  RepeatTypeComponent,
  SelectFieldCosmos,
  SHMDataChannelsComponent,
} from '@cosmos/angular/ui';
import { ThemeService } from './services/theme.service';
import { provideMarkdown } from 'ngx-markdown'
import { firstValueFrom } from 'rxjs';

export const appConfig: ApplicationConfig = {
  providers: [
    // ROUTER
    provideRouter(routes, withComponentInputBinding()),
    // SERVICE WORKER
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production, // Disable in development, enable in production
    }),
    // auto-reload as soon as a new SW version finishes downloading, so open
    // tabs don't get stuck running a stale build (see AppUpdateService)
    provideAppInitializer(() => inject(AppUpdateService).init()),
    // INTERNATIONALIZATION
    provideTranslateService({
      loader: provideTranslateHttpLoader(),
      fallbackLang: 'es_ES',
      lang: 'es_ES',
    }),
    // HTTP — CSRF header injection is handled in AuthInterceptor (cross-origin compatible)
    provideHttpClient(
      withInterceptorsFromDi(),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // DYNAMIC FORMS
    provideFormlyCore([
      // ...withFormlyMaterial(),
      ...withFormlyPrimeNG(),
      {
        types: [
          {
            name: 'chips-number',
            wrappers: ['form-field'],
            component: FormlyChipsNumberComponent,
            defaultOptions: { defaultValue: [] },
          },
          { name: 'input', component: InputTextFieldCosmos },
          { name: 'input-number', component: InputNumberFieldCosmos },
          { name: 'select', component: SelectFieldCosmos },
          { name: 'multiselect', component: MultiSelectFieldCosmos },
          { name: 'repeat', component: RepeatTypeComponent },
          { name: 'shm-channels', component: SHMDataChannelsComponent },
        ],
        wrappers: [
          { name: 'float-label-wrapper', component: PrimeNGFloatLabelWrapper },
          { name: 'default-wrapper', component: PrimeNGDefaultWrapper },
          { name: 'checkbox-wrapper', component: PrimeNGCheckboxWrapper },
          { name: 'group-wrapper', component: GroupWrapper },
        ],
      },
    ]),
    // INITIALIZERS
    // posthog — MUST run before the auth initializer so AuthService.updateCurrentUser()
    // can identify the user against a live PostHog client on startup.
    provideAppInitializer(() => {
      initPostHog(inject(PostHogService))();
    }),
    // auth — unconditionally calls GET /users/current; browser sends cosmos_session cookie automatically
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const stateService = inject(StateService);
      inject(ThemeService); // constructed here so its subscription is active before user resolves

      return firstValueFrom(authService.updateCurrentUser())
        .then(() => { stateService.markInitialized(); return true; })
        .catch((error) => {
          console.error('[AppInit] Failed to fetch user:', error);
          authService.purgeAuth();
          stateService.markInitialized();
          return false;
        });
    }),
    // config
    provideAppInitializer(initConfig()),
    // monitoring
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // STYLES
    provideAnimations(),
    provideMarkdown(),
    providePrimeNG({
      theme: {
        preset: getInitialPreset(),
        options: { darkModeSelector: '.dark' },
      },
    }),

    // PIPES
    DatePipe,
    DecimalPipe,
    // PRIMENG GLOBAL SERVICES
    MessageService,
  ],
};
